import { parse as parseSDF } from 'sdf-parser';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { makeOneSDF } from 'BMapsUtil/mol_format_utils';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';
import { gninaProperties } from './GninaScoringJob';

/**
 * Gnina docking job: docks compounds and produces scored poses.
 *
 * Docking writes its scored properties under the `gnina_scoring` scope
 * (`propertyScope`) so they share columns with Gnina Scoring, but its
 * results-table layout differs, so it overrides `getDataInfo` and omits
 * `scopedPropertyMetadata`.
 *
 * @augments JobDefinition
 */
export class GninaDockingJob extends JobDefinition {
    static jobTypeId = 'gnina_docking';
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'Gnina Docking';
    static jobName = 'Gnina Docking Job';
    static jobDescription = 'places and scores compound poses within a protein structure.\n'
        + 'Each result is a 3D pose specific to the structure it was docked against.';

    static get propertyScope() { return 'gnina_scoring'; }

    /**
     * Results-table columns for docked poses. Distinct from the scoped
     * properties: it adds per-pose `name`/`poseI` columns and reuses the
     * Gnina property metadata for the score columns.
     *
     * Note: Gnina docking adds properties under the `gnina_scoring` scope
     * (determined by `propertyScope`) but when displayed in the table it has
     * different requirements. `scopedPropertyMetadata` is omitted to prevent
     * conflicts with Gnina Scoring, and `dataInfo` is overridden to provide
     * compound-table values.
     * @override
     * @returns {Object<string, import('../JobDefinition').DataInfoEntry>}
     */
    static getDataInfo() {
        return {
            SMILES: {
                label: 'Structure', structureValue: true, defaultVisible: true, propApplicability: PropApplicability.PERPOSE,
            },
            sourceSpec: {
                label: 'Source Cmpd', type: 'string', defaultVisible: true, propApplicability: PropApplicability.PERPOSE,
            },
            poseI: { label: 'Pose #', defaultVisible: true, propApplicability: PropApplicability.PERPOSE },
            affinity: gninaProperties.affinity.metadata,
            cnn_affinity: gninaProperties.cnn_affinity.metadata,
            cnn_score: gninaProperties.cnn_score.metadata,
            cnn_vs: gninaProperties.cnn_vs.metadata,
            cnn_variance: gninaProperties.cnn_variance.metadata,
            // `sdf` is temporarily 3D-only: today the sole consumer is the results tab's
            // "Download 3D SDF", and only the job types returning 3D poses populate it. The name
            // is the general format, so resist reading it as "the SDF for this row" — a 2D job
            // type's rows would need their own source rather than this key.
            // Row plumbing, not results: without these the grid adds raw `id` and `sdf` columns.
            id: { excludeFromTable: true },
            sdf: { excludeFromTable: true, excludeFromImport: true },
        };
    }

    /**
     * Parses the docking SDF into pose records and stores them on local job
     * state. Poses are imported into the workspace separately (see
     * `ComputationCmds`), which is where the `gnina_scoring` properties are
     * applied to compounds.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        let resultsPacket = results;
        let poses = [];
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket.jobResults) return { error: 'Empty results' };
            const cmpdSmiles = resultsPacket.stash?.cmpdSmiles || {};
            const parsed = parseSDF(resultsPacket.jobResults.sdf);
            poses = parsed.molecules.map((entry, i) => {
                const sourceSpec = entry.bmapsResSpec;
                const smiles = cmpdSmiles[sourceSpec]?.smiles || null;
                const sdf = makeOneSDF(entry.molfile, { tmpSpec: sourceSpec, tmpI: i });
                return {
                    id: `${job.jobid}_${i}`,
                    sourceSpec,
                    poseI: i + 1,
                    SMILES: smiles,
                    sdf,
                    affinity: entry.minimizedAffinity,
                    cnn_score: entry.CNNscore,
                    cnn_affinity: entry.CNNaffinity,
                    cnn_vs: entry.CNN_VS,
                    cnn_variance: entry.CNNaffinity_variance,
                };
            });
            updateLocalJobState(job.jobid, { structures: poses });
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return {
            error,
            results: { poses, jobResults: resultsPacket.jobResults },
        };
    }
}
